/**
 * How the shop groups products, worked out from the product data itself:
 * the jewellery type comes from category names ("Opal Pendants", "Triplet
 * Studs"…), the opal type from `opal_type` and category names. Anything that
 * is neither is a souvenir/gift. Pure functions, shared by the landing page,
 * catalogue and product page.
 */

export const OPAL_TYPES = ["Solid", "Black", "Boulder", "Doublet", "Triplet"];
export const JEWELLERY_TYPES = ["Rings", "Pendants", "Earrings"];
export const CATEGORY_GROUPS = ["Opal jewellery", "Souvenirs & gifts"];

const categoryNames = (product) =>
  (product.categories || []).map((c) => (typeof c === "string" ? c : c.name || "")).join(" | ").toLowerCase();

export function jewelleryType(product) {
  const names = categoryNames(product) + " | " + (product.name || "").toLowerCase();
  if (/\brings?\b/.test(names)) return "Rings";
  if (/pendant|necklace/.test(names)) return "Pendants";
  if (/earring|\bstuds?\b/.test(names)) return "Earrings";
  return null;
}

export function opalType(product) {
  const text = `${product.opal_type || ""} | ${categoryNames(product)}`.toLowerCase();
  if (/doublet/.test(text)) return "Doublet";
  if (/triplet/.test(text)) return "Triplet";
  if (/black/.test(text) && /opal/.test(text)) return "Black";
  if (/boulder/.test(text)) return "Boulder";
  if (/solid|crystal|white|light opal|fire|matrix|natural/.test(text)) return "Solid";
  return null;
}

export function categoryGroup(product) {
  return jewelleryType(product) || opalType(product) ? "Opal jewellery" : "Souvenirs & gifts";
}

export const isSouvenir = (product) => categoryGroup(product) === "Souvenirs & gifts";

/** "Solid opal · Pendant", or "Souvenir". */
export function kicker(product) {
  const opal = opalType(product);
  const jewel = jewelleryType(product);
  if (!opal && !jewel) return "Souvenir";
  const piece = jewel ? jewel.replace(/s$/, "") : "Jewellery";
  return opal ? `${opal} opal · ${piece}` : `Opal · ${piece}`;
}

export function attribute(product, name) {
  const attr = (product.attributes || []).find((a) => (a.name || "").toLowerCase() === name.toLowerCase());
  const value = attr && attr.options && attr.options[0];
  return value ? String(value) : "";
}

/** Prices as numbers, with the store's existing sale rules. */
export function pricing(product) {
  const price = parseFloat(product.price || product.regular_price || 0) || 0;
  const regular = parseFloat(product.regular_price || 0) || 0;
  const sale = product.sale_price ? parseFloat(product.sale_price) : null;
  const onSale = !!sale && sale < regular;
  return { price: onSale ? sale : price, regular, onSale };
}

export const isOutOfStock = (product) => product.stock_status === "outofstock";

export const productId = (product) => product._id || product.id;

export const mainImage = (product) => {
  const images = [...(product.images || [])].sort((a, b) => (a.position || 0) - (b.position || 0));
  return images.length ? images[0].src : "";
};

export const METALS = {
  sterling_silver: {
    label: "Sterling silver",
    long: "925 sterling silver",
    swatch: "linear-gradient(135deg,#f4f4f4,#b9b9b9)",
  },
  gold_plated: {
    label: "Gold plated",
    long: "Gold plated over 925 sterling silver",
    swatch: "linear-gradient(135deg,#f6dc8a,#c99a2e)",
  },
};

export const SORTS = [
  ["featured", "Featured"],
  ["low", "Price: low to high"],
  ["high", "Price: high to low"],
  ["name", "Name A–Z"],
];

export function sortProducts(list, sort) {
  const out = [...list];
  // "Call for Price" items sort after priced ones when sorting by price
  const p = (x) => pricing(x).price || Infinity;
  if (sort === "low") out.sort((a, b) => p(a) - p(b));
  else if (sort === "high") out.sort((a, b) => (pricing(b).price || 0) - (pricing(a).price || 0));
  else if (sort === "name") out.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  // Featured: featured pieces, then opal jewellery, then souvenirs (stable otherwise)
  else out.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0) || (isSouvenir(a) ? 1 : 0) - (isSouvenir(b) ? 1 : 0));
  return out;
}
